import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight, Play } from "lucide-react";
import WordReveal from "@/components/WordReveal";
import RollingCounter from "@/components/RollingCounter";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import Placeholder from "@/components/Placeholder";

const CAUSES = [
  { key: "natures-dividend", title: "Nature's Dividend", desc: "Investing in nature, creating lasting impact.", tint: "ember", href: "/events?category=service", image: "/images/home/causes/natures-dividend.jpg" },
  { key: "naari-care", title: "Naari Care", desc: "Empowering young girls through dignity, access, and care.", tint: "gold", href: "/events?category=service", image: "/images/home/causes/naari-care.jpg" },
  { key: "mauka-e-manch", title: "Mauka E Manch", desc: "Where talent meets opportunity, and confidence takes centre stage.", tint: "maroon", href: "/events?category=leadership", image: "/images/home/causes/mauka-e-manch.jpg" },
  { key: "voice", title: "Voice for the Voiceless", desc: "Advocating for animal welfare and care.", tint: "ink", href: "/events?category=service", image: "/images/home/causes/voice-for-the-voiceless.jpg" },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const parallaxY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const parallaxScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <div className="pt-20">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[92vh] flex items-end overflow-hidden">
        <motion.div
          style={{ y: parallaxY, scale: parallaxScale }}
          className="absolute inset-0"
        >
          {/* Fallback background — visible if the video fails to load */}
          <div className="absolute inset-0 bg-[#8B1E2D]" style={{ zIndex: 0 }} />

          {/* Hero background video */}
          <video
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            aria-hidden="true"
            src="/hero.mp4"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ zIndex: 0 }}
            data-testid="hero-video"
          />

          {/* Dark overlay for text legibility */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: "rgba(0,0,0,0.45)", zIndex: 1 }}
          />
        </motion.div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-32 w-full" style={{ zIndex: 2 }}>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8 flex items-center gap-3 font-inter text-[11px] tracking-[0.3em] uppercase text-[#F4C430]"
          >
            <span className="w-10 h-px bg-[#F4C430]" />
            Leo Club of Mumbai Carter Road
          </motion.div>

          <WordReveal
            text="Roar. Rise. Radiate."
            className="font-playfair font-bold text-white text-[14vw] sm:text-[10vw] md:text-[8vw] lg:text-[7.5rem] leading-[0.95] tracking-tight"
            testId="hero-headline"
          />

          <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 1.4, ease: [0.16, 1, 0.3, 1] }}
              className="md:col-span-6 font-inter text-white/85 text-lg md:text-xl leading-relaxed max-w-xl"
            >
              A youth-led movement creating real impact across Mumbai — through service, leadership, and community.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 1.6, ease: [0.16, 1, 0.3, 1] }}
              className="md:col-span-6 flex flex-wrap gap-4 md:justify-end"
            >
              <Link
                to="/join"
                className="btn-glow inline-flex items-center gap-2 bg-[#F4C430] text-[#1A1A1A] px-7 py-3.5 rounded-full font-inter text-sm tracking-wide hover:bg-white transition-colors"
                data-testid="hero-cta-join"
              >
                Join the Community <ArrowUpRight size={16} />
              </Link>
              <Link
                to="/events"
                className="btn-glow inline-flex items-center gap-2 border border-white/40 text-white px-7 py-3.5 rounded-full font-inter text-sm tracking-wide hover:border-[#F4C430] hover:text-[#F4C430] transition-colors"
                data-testid="hero-cta-explore"
              >
                Explore Our Work
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* IMPACT STRIP — Glassmorphism cards */}
      <section className="bg-[#7F1B29] text-white py-24 md:py-32 relative overflow-hidden" data-testid="impact-strip">
        {/* Ambient backlight glows for glassmorphism */}
        <div className="absolute -top-20 -left-20 w-[500px] h-[500px] rounded-full bg-[#8B1E2D] opacity-30 blur-[140px] pointer-events-none" />
        <div className="absolute -bottom-20 -right-20 w-[500px] h-[500px] rounded-full bg-[#E76F2E] opacity-20 blur-[160px] pointer-events-none" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-[#F4C430] opacity-[0.06] blur-[180px] pointer-events-none" />

        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 relative z-10">
          <Reveal className="mb-14 flex items-center gap-3 font-inter text-[11px] tracking-[0.3em] uppercase text-[#F4C430]">
            <span className="w-10 h-px bg-[#F4C430]" /> The impact, so far
          </Reveal>
          <Stagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {[
              { v: 30000, s: "+", label: "Lives Impacted", testId: "impact-lives" },
              { v: 3000, s: "+", label: "Volunteer Hours", testId: "impact-hours" },
              { v: 30, s: "+", label: "Active Members", testId: "impact-members" },
              { v: 2017, s: "", label: "Serving Since", testId: "impact-since", format: false },
            ].map((it, idx) => (
              <StaggerItem key={it.label}>
                <div
                  className="group relative rounded-2xl p-7 md:p-8 h-full overflow-hidden transition-all duration-500 ease-[cubic-bezier(0.25,1,0.5,1)] hover:-translate-y-1.5 hover:shadow-[0_24px_60px_-20px_rgba(244,196,48,0.2)]"
                  style={{
                    background: "linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 100%)",
                    backdropFilter: "blur(18px)",
                    WebkitBackdropFilter: "blur(18px)",
                    border: "1px solid rgba(255,255,255,0.12)",
                    boxShadow: "inset 0 1px 0 0 rgba(255,255,255,0.15), 0 12px 40px -12px rgba(0,0,0,0.4)",
                  }}
                >
                  {/* Inner top-edge highlight */}
                  <div className="absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none" />

                  <div className="font-inter text-[10px] tracking-[0.3em] uppercase text-[#F4C430] mb-5">
                    0{idx + 1} · Impact
                  </div>
                  <div
                    className="font-playfair font-bold text-4xl md:text-5xl lg:text-[3.5rem] text-white leading-[0.95] tracking-tight whitespace-nowrap"
                    style={{ willChange: "transform", transform: "translateZ(0)" }}
                  >
                    <RollingCounter value={it.v} suffix={it.s} testId={it.testId} format={it.format !== false} />
                  </div>
                  <div className="mt-6 pt-5 border-t border-white/10 font-inter text-[11px] md:text-xs tracking-[0.25em] uppercase text-white/70 font-light">
                    {it.label}
                  </div>

                  {/* Hover glow accent */}
                  <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                    style={{ boxShadow: "inset 0 0 0 1px rgba(244,196,48,0.3)" }}
                  />
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* ABOUT PREVIEW */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
          <Reveal className="md:col-span-6">
            <div
              className="aspect-[4/5] w-full overflow-hidden"
              style={{
                backgroundImage: "url(/images/home/home-about-preview.jpg)",
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
              role="img"
              aria-label="Leo Club members in action"
            />
          </Reveal>
          <Reveal delay={0.15} className="md:col-span-6">
            <div className="font-inter text-[11px] tracking-[0.3em] uppercase text-[#E76F2E] mb-5">
              <span className="w-8 h-px bg-[#E76F2E] inline-block align-middle mr-3" />
              Who we are
            </div>
            <h2 className="font-playfair text-4xl md:text-5xl lg:text-6xl font-semibold text-[#2C2C2C] leading-[1.05]">
              More than a club — <em className="text-[#8B1E2D]">a space for action</em>.
            </h2>
            <div className="mt-8 space-y-5 font-inter text-[#555555] text-base md:text-lg leading-relaxed">
              <p>
                The Leo Club of Mumbai Carter Road is a space where young people turn intent into action.
              </p>
              <p>
                From education and environmental initiatives to health and animal welfare, we create meaningful impact while growing together.
              </p>
            </div>
            <Link
              to="/about"
              className="btn-glow mt-10 inline-flex items-center gap-2 border border-[#2C2C2C] text-[#2C2C2C] px-7 py-3.5 rounded-full font-inter text-sm tracking-wide hover:bg-[#2C2C2C] hover:text-white transition-colors"
              data-testid="about-preview-cta"
            >
              Learn More <ArrowUpRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* CAUSES */}
      <section className="py-24 md:py-32 bg-[#F7F3EE]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16 items-end">
            <Reveal className="md:col-span-7">
              <div className="font-inter text-[11px] tracking-[0.3em] uppercase text-[#E76F2E] mb-5">
                <span className="w-8 h-px bg-[#E76F2E] inline-block align-middle mr-3" />
                Our legacies
              </div>
              <h2 className="font-playfair text-4xl md:text-5xl lg:text-6xl font-semibold text-[#2C2C2C] leading-[1.05]">
                The work that <em className="text-[#8B1E2D]">moves</em> us.
              </h2>
            </Reveal>
            <Reveal delay={0.15} className="md:col-span-5">
              <p className="font-inter text-[#555555] text-base md:text-lg leading-relaxed">
                Each initiative is rooted in a real need — shaped by the people we work with and the moments that move us.
              </p>
            </Reveal>
          </div>

          <Stagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" stagger={0.1}>
            {CAUSES.map((c, i) => (
              <StaggerItem key={c.key}>
                <Link
                  to={c.href}
                  className="group block bg-white border border-black/5 p-7 h-full transition-all duration-500 ease-[cubic-bezier(0.25,1,0.5,1)] hover:-translate-y-1.5 hover:shadow-[0_24px_50px_-20px_rgba(139,30,45,0.25)]"
                  data-testid={`cause-card-${c.key}`}
                >
                  <article className="flex flex-col h-full">
                    <div className="overflow-hidden mb-6">
                      {c.image ? (
                        <div className="aspect-[4/3] w-full overflow-hidden">
                          <img
                            src={c.image}
                            alt={c.title}
                            loading="lazy"
                            className="w-full h-full object-cover transition-transform ease-out group-hover:scale-105"
                            style={{ transitionDuration: "400ms" }}
                          />
                        </div>
                      ) : (
                        <div className="transition-transform duration-700 ease-[cubic-bezier(0.25,1,0.5,1)] group-hover:scale-105">
                          <Placeholder tint={c.tint} ratio="aspect-[4/3]" label={c.title} index={`0${i + 1}`} solid />
                        </div>
                      )}
                    </div>
                    <h3 className="font-playfair text-2xl md:text-3xl font-semibold text-[#2C2C2C] leading-tight">
                      {c.title}
                    </h3>
                    <p className="mt-3 font-inter text-[#555555] text-sm md:text-base leading-relaxed flex-1">
                      {c.desc}
                    </p>
                    <div className="mt-6 pt-5 border-t border-black/5 flex items-center justify-between font-inter text-[11px] tracking-[0.25em] uppercase text-[#8B1E2D]">
                      <span>Initiative 0{i + 1}</span>
                      <ArrowUpRight size={16} className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                    </div>
                  </article>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* ROAR ROOM */}
      <section className="py-24 md:py-32 bg-[#1A1A1A] text-white relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <Reveal className="md:col-span-5">
            <div className="font-inter text-[11px] tracking-[0.3em] uppercase text-[#F4C430] mb-5">
              <span className="w-8 h-px bg-[#F4C430] inline-block align-middle mr-3" />
              The Roar Room
            </div>
            <h2 className="font-playfair text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.05]">
              Stories that <em className="text-[#F4C430]">shape</em> the way we lead.
            </h2>
            <p className="mt-6 font-inter text-white/70 text-base md:text-lg leading-relaxed max-w-md">
              Conversations on leadership, growth, and the real experiences that change how we think, work, and belong.
            </p>
            <a
              href="https://www.youtube.com/@leoclubofmcr/videos"
              target="_blank"
              rel="noreferrer"
              className="btn-glow mt-10 inline-flex items-center gap-2 bg-[#F4C430] text-[#1A1A1A] px-7 py-3.5 rounded-full font-inter text-sm tracking-wide hover:bg-white transition-colors"
              data-testid="roar-watch-episodes"
            >
              Watch Episodes <ArrowUpRight size={16} />
            </a>
          </Reveal>

          <div className="md:col-span-7 grid grid-cols-2 gap-4 md:gap-5">
            {[
              { id: "5YhiWbFpR-Y", url: "https://youtu.be/5YhiWbFpR-Y?si=GdykTA1PGB02fPxR" },
              { id: "SGNdj8yORqU", url: "https://youtu.be/SGNdj8yORqU?si=tN4CQCgQFoHVnpRo" },
              { id: "p5ElBjLarDw", url: "https://youtu.be/p5ElBjLarDw?si=iHTCzYEGxPE5FljG" },
              { id: "B09q5AxHUjw", url: "https://youtu.be/B09q5AxHUjw?si=CvC4d9txGS7wCWzM" },
            ].map((ep, i) => {
              const Wrapper = ep.comingSoon ? "div" : "a";
              const wrapperProps = ep.comingSoon
                ? { "aria-label": "Episode 4 — coming soon" }
                : { href: ep.url, target: "_blank", rel: "noreferrer" };
              return (
                <Reveal key={i} delay={i * 0.08}>
                  <Wrapper
                    {...wrapperProps}
                    className={`group block relative overflow-hidden aspect-video ${ep.comingSoon ? "cursor-default bg-[#1A1A1A]" : "cursor-pointer"}`}
                    data-testid={`roar-episode-${i + 1}`}
                  >
                    {!ep.comingSoon ? (
                      <>
                        <img
                          src={`https://img.youtube.com/vi/${ep.id}/maxresdefault.jpg`}
                          alt=""
                          loading="lazy"
                          className="w-full h-full object-cover transition-transform duration-700 ease-[cubic-bezier(0.25,1,0.5,1)] group-hover:scale-105"
                          onError={(e) => {
                            e.currentTarget.src = `https://img.youtube.com/vi/${ep.id}/hqdefault.jpg`;
                          }}
                        />
                        <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors duration-500" />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-14 h-14 rounded-full bg-white/95 flex items-center justify-center animate-pulse-soft shadow-xl">
                            <Play size={18} className="text-[#8B1E2D] ml-0.5" fill="#8B1E2D" />
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center border border-white/10">
                        <span className="font-inter text-[11px] tracking-[0.3em] uppercase text-white/60">
                          Coming Soon
                        </span>
                      </div>
                    )}
                  </Wrapper>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* FELLOWSHIP */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
          <Reveal className="md:col-span-6 order-2 md:order-1">
            <div className="font-inter text-[11px] tracking-[0.3em] uppercase text-[#E76F2E] mb-5">
              <span className="w-8 h-px bg-[#E76F2E] inline-block align-middle mr-3" />
              Fellowship
            </div>
            <h2 className="font-playfair text-4xl md:text-5xl lg:text-6xl font-semibold text-[#2C2C2C] leading-[1.05]">
              More than <em className="text-[#8B1E2D]">just service</em>.
            </h2>
            <p className="mt-8 font-inter text-[#555555] text-base md:text-lg leading-relaxed max-w-xl">
              We're not just working together — we're growing together. Through shared experiences, friendships, and collaboration, we build a community that makes every effort more meaningful.
            </p>
          </Reveal>
          <div className="md:col-span-6 order-1 md:order-2 grid grid-cols-6 grid-rows-6 gap-3 md:gap-4 h-[480px] md:h-[560px]">
            <Reveal className="col-span-4 row-span-4" delay={0}>
              <div className="group relative h-full overflow-hidden">
                <div
                  className="absolute inset-0 transition-transform duration-[400ms] ease-in-out group-hover:scale-105"
                  style={{
                    backgroundImage: "url(/images/home/home-fellowship-candid.jpg)",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                />
              </div>
            </Reveal>
            <Reveal className="col-span-2 row-span-3" delay={0.12}>
              <div className="group relative h-full overflow-hidden">
                <div
                  className="absolute inset-0 transition-transform duration-[400ms] ease-in-out group-hover:scale-105"
                  style={{
                    backgroundImage: "url(/images/home/home-fellowship-laughter.jpg)",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                />
              </div>
            </Reveal>
            <Reveal className="col-span-2 row-span-3" delay={0.2}>
              <div className="group relative h-full overflow-hidden">
                <div
                  className="absolute inset-0 transition-transform duration-[400ms] ease-in-out group-hover:scale-105"
                  style={{
                    backgroundImage: "url(/images/home/home-fellowship-bonding.jpg)",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                />
              </div>
            </Reveal>
            <Reveal className="col-span-6 row-span-2" delay={0.3}><Placeholder tint="ink" ratio="h-full" className="h-full" solid label="Together, always." /></Reveal>
          </div>
        </div>
      </section>

      {/* JOIN BANNER */}
      <section className="py-24 md:py-32 bg-[#8B1E2D] text-white relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-100" style={{ mixBlendMode: "overlay" }} />
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 relative z-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <Reveal className="md:col-span-8">
            <div className="font-inter text-[11px] tracking-[0.3em] uppercase text-[#F4C430] mb-5">
              <span className="w-8 h-px bg-[#F4C430] inline-block align-middle mr-3" />
              Come be a part of it
            </div>
            <h2 className="font-playfair text-5xl md:text-6xl lg:text-7xl font-semibold leading-[1.02]">
              A space to <em className="text-[#F4C430]">contribute, connect,</em> and grow.
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="md:col-span-4 md:text-right">
            <p className="font-inter text-white/80 text-base md:text-lg leading-relaxed mb-8">
              If you've been looking for something meaningful — you'll feel at home here.
            </p>
            <div className="flex flex-wrap gap-4 md:justify-end">
              <Link
                to="/join"
                className="btn-glow inline-flex items-center gap-2 bg-[#F4C430] text-[#1A1A1A] px-7 py-3.5 rounded-full font-inter text-sm tracking-wide hover:bg-white transition-colors"
                data-testid="home-join-cta"
              >
                Join Us <ArrowUpRight size={16} />
              </Link>
              <Link
                to="/contact"
                className="btn-glow inline-flex items-center border border-white/40 text-white px-7 py-3.5 rounded-full font-inter text-sm tracking-wide hover:border-[#F4C430] hover:text-[#F4C430] transition-colors"
                data-testid="home-involved-cta"
              >
                Get Involved
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
